<template>
  <div class="subscription-page">
    <div class="container">
      <div class="header">
        <h1>해피테이블 정기결제</h1>
        <p>따뜻한 봄날같은 정기결제</p>
      </div>

      <!-- 정기결제 폼 -->
      <div class="form-section">
        <h3>정기결제 정보 입력</h3>

        <div class="form-row">
          <div class="form-group">
            <label>월 청구금액</label>
            <div class="form-value">165,000원</div>
          </div>

          <div class="form-group">
            <label>결제기간</label>
            <div class="form-value">60개월</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group full-width">
            <label for="organizationName">기관이름</label>
            <input
              type="text"
              id="organizationName"
              v-model="subscriptionForm.organizationName"
              placeholder="예: 홍길동 교육원"
              class="form-input"
              required
            />
            <small class="form-hint"
              >청구서에 표시될 기관명을 입력해주세요</small
            >
          </div>
        </div>

        <!-- 정기결제 계산 결과 표시 -->
        <div v-if="showSummary" class="subscription-summary">
          <h4>📊 정기결제 요약</h4>
          <div class="summary-grid">
            <div class="summary-item">
              <span class="label">월 결제금액</span>
              <span class="value">{{ formatCurrency(monthlyAmount) }} </span>
            </div>

            <div class="summary-item">
              <span class="label">결제기간</span>
              <span class="value">{{ paymentPeriod }}개월</span>
            </div>
            <div class="summary-item">
              <span class="label">총 결제금액</span>
              <span class="value">{{ formatCurrency(totalAmount) }}</span>
            </div>
            <div class="summary-item">
              <span class="label">기관명</span>
              <span class="value">{{ subscriptionForm.organizationName }}</span>
            </div>
            <div class="summary-item">
              <span class="label">결제일</span>
              <span class="value">매월 1일</span>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button
            type="button"
            @click="createSubscription"
            class="btn-create-subscription"
            :disabled="!isFormValid || isCreatingSubscription"
          >
            <i class="fas fa-credit-card"></i>
            <span v-if="isCreatingSubscription">정기결제 등록중...</span>
            <span v-else>정기결제 등록</span>
          </button>
        </div>
      </div>

      <!-- 생성된 결제 정보 표시 -->
      <div v-if="subscriptionCreated" class="subscription-result">
        <h4>✅ 정기결제 등록 완료</h4>
        <div class="result-box">
          <div class="result-info">
            <div class="result-details">
              <strong>{{ subscriptionForm.organizationName }}</strong>
              <span class="amount">{{ formatCurrency(monthlyAmount) }}</span>
              <span class="period">(매월 1일 × {{ paymentPeriod }}개월)</span>
            </div>
            <div class="billing-info">
              <p>빌링키: {{ billingKey }}</p>
              <p>고객키: {{ customerKey }}</p>
              <p>등록일: {{ formatDate(new Date()) }}</p>
            </div>
          </div>
        </div>

        <div class="result-actions">
          <button @click="resetForm" class="btn-reset">
            <i class="fas fa-refresh"></i>
            <span class="btn-text">새로운 정기결제 등록</span>
          </button>
          <button @click="testPayment" class="btn-test">
            <i class="fas fa-play"></i>
            <span class="btn-text">즉시 결제 테스트</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 성공 팝업 -->
    <div
      v-if="showSuccessPopup"
      class="popup-overlay"
      @click="closePopup('success')"
    >
      <div class="popup success" @click.stop>
        <div class="popup-icon">
          <i class="fas fa-check"></i>
        </div>
        <h3>결제 등록 완료!</h3>
        <p>
          정기결제가 성공적으로 등록되었습니다.<br />매월 1일에 자동으로
          결제됩니다.
        </p>
        <button class="popup-button" @click="closePopup('success')">
          확인
        </button>
      </div>
    </div>

    <!-- 실패 팝업 -->
    <div
      v-if="showErrorPopup"
      class="popup-overlay"
      @click="closePopup('error')"
    >
      <div class="popup error" @click.stop>
        <div class="popup-icon">
          <i class="fas fa-times"></i>
        </div>
        <h3>결제 등록 실패</h3>
        <p>{{ errorMessage }}</p>
        <button class="popup-button" @click="closePopup('error')">확인</button>
      </div>
    </div>

    <!-- App.vue에서 가져온 결제 로딩 오버레이 -->
    <div v-if="isPaymentProcessing" class="payment-loading-overlay">
      <div class="payment-loading-content">
        <div class="loading-spinner"></div>
        <h3>결제 처리중...</h3>
        <p>잠시만 기다려주세요.</p>
      </div>
    </div>

    <!-- App.vue에서 가져온 전역 알림 메시지 -->
    <transition name="notification">
      <div
        v-if="notification.show"
        :class="['notification', notification.type]"
      >
        <i :class="getNotificationIcon(notification.type)"></i>
        <span>{{ notification.message }}</span>
        <button @click="closeNotification" class="notification-close">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "TossSubscriptionPage",
  data() {
    return {
      subscriptionForm: {
        monthlyAmount: 165000, // 고정값: 16만원
        paymentPeriod: 60, // 고정값: 60개월
        organizationName: "",
      },

      isCreatingSubscription: false,
      subscriptionCreated: false,
      showSuccessPopup: false,
      showErrorPopup: false,
      errorMessage: "",

      // 결제 결과 데이터
      billingKey: "",
      customerKey: "",
      subscriptionId: "",

      // 토스페이먼츠 객체
      tossPayments: null,

      isAdmin: false,

      // App.vue에서 가져온 상태들
      isPaymentProcessing: false,
      notification: {
        show: false,
        type: "info", // 'success', 'error', 'warning', 'info'
        message: "",
      },
    };
  },

  computed: {
    monthlyAmount() {
      return this.subscriptionForm.monthlyAmount; // 고정값 160000
    },

    paymentPeriod() {
      return this.subscriptionForm.paymentPeriod; // 고정값 60
    },

    totalAmount() {
      return this.monthlyAmount * this.paymentPeriod;
    },

    showSummary() {
      return (
        this.monthlyAmount > 0 &&
        this.paymentPeriod > 0 &&
        this.subscriptionForm.organizationName.trim() !== ""
      );
    },

    isFormValid() {
      return (
        this.monthlyAmount >= 1000 &&
        this.paymentPeriod >= 1 &&
        this.paymentPeriod <= 120 &&
        this.subscriptionForm.organizationName.trim() !== ""
      );
    },
  },

  mounted() {
    this.initTossPayments();
    this.checkUrlParams();
    this.checkAdminPermission();
  },

  methods: {
    // ======= 포맷팅 및 유틸리티 함수들 (내부 메서드로 변환) =======

    // 날짜 포맷팅
    formatDate(date) {
      return new Date(date).toLocaleDateString("ko-KR");
    },

    // 통화 포맷팅
    formatCurrency(amount) {
      return amount.toLocaleString() + "원";
    },

    // 주문 ID 생성
    generateOrderId() {
      return (
        "order_" +
        Date.now() +
        "_" +
        Math.random()
          .toString(36)
          .substr(2, 9)
      );
    },

    // 고객 키 생성
    generateCustomerKey() {
      return (
        "customer_" +
        Date.now() +
        "_" +
        Math.random()
          .toString(36)
          .substr(2, 9)
      );
    },

    // 결제 상태 텍스트 변환
    getPaymentStatusText(status) {
      const statusMap = {
        READY: "결제 대기",
        IN_PROGRESS: "결제 진행중",
        WAITING_FOR_DEPOSIT: "입금 대기",
        DONE: "결제 완료",
        CANCELED: "결제 취소",
        PARTIAL_CANCELED: "부분 취소",
        ABORTED: "결제 중단",
        EXPIRED: "결제 만료",
      };
      return statusMap[status] || status;
    },

    // 토스페이먼츠 에러 메시지 한국어 변환
    getTossErrorMessage(errorCode) {
      const errorMessages = {
        INVALID_REQUEST: "잘못된 요청입니다.",
        NOT_FOUND: "존재하지 않는 결제정보입니다.",
        FORBIDDEN: "권한이 없습니다.",
        UNAUTHORIZED: "인증에 실패했습니다.",
        NOT_ALLOWED_POINT_USE: "포인트 사용이 불가합니다.",
        INVALID_API_KEY: "API 키가 유효하지 않습니다.",
        INVALID_SECRET_KEY: "Secret 키가 유효하지 않습니다.",
        INVALID_BILLING_KEY: "빌링키가 유효하지 않습니다.",
        ALREADY_PROCESSED: "이미 처리된 결제입니다.",
        PROVIDER_ERROR: "결제 서비스 오류가 발생했습니다.",
        USER_CANCEL: "사용자가 결제를 취소했습니다.",
        INVALID_CARD: "유효하지 않은 카드입니다.",
        INSUFFICIENT_FUNDS: "잔액이 부족합니다.",
        EXPIRED_CARD: "만료된 카드입니다.",
        INVALID_CARD_COMPANY: "지원하지 않는 카드사입니다.",
        INVALID_CARD_NUMBER: "잘못된 카드번호입니다.",
        INVALID_UNREGISTERED_SUBMALL: "등록되지 않은 서브몰입니다.",
        INVALID_CARD_EXPIRATION: "카드 유효기간이 잘못되었습니다.",
        INVALID_STOPPED_CARD: "정지된 카드입니다.",
        EXCEED_MAX_CARD_INSTALLMENT_PLAN: "할부 개월 수가 초과되었습니다.",
        INVALID_CARD_INSTALLMENT_PLAN: "유효하지 않은 할부 개월 수입니다.",
        NOT_SUPPORTED_INSTALLMENT_PLAN_CARD_OR_MERCHANT:
          "할부를 지원하지 않는 카드 또는 가맹점입니다.",
        INVALID_CARD_LOST_OR_STOLEN: "분실/도난 카드입니다.",
        RESTRICTED_TRANSFER_ACCOUNT: "계좌이체 제한 계좌입니다.",
        INVALID_CARD_CVC: "CVC가 잘못되었습니다.",
        INVALID_PASSWORD: "비밀번호가 잘못되었습니다.",
        INCORRECT_BASIC_AUTH_FORMAT: "잘못된 인증 형식입니다.",
        INVALID_AUTHORIZATION: "유효하지 않은 인증입니다.",
        EXCEED_MAX_AMOUNT: "최대 금액을 초과했습니다.",
        EXCEED_MAX_DAILY_AMOUNT: "일일 최대 금액을 초과했습니다.",
        NOT_SUPPORTED_CARD: "지원하지 않는 카드입니다.",
      };
      return errorMessages[errorCode] || "알 수 없는 오류가 발생했습니다.";
    },

    // ======= App.vue에서 가져온 메서드들 =======

    // 결제 처리 상태 관리
    setPaymentProcessing(status) {
      this.isPaymentProcessing = status;
    },

    // 전역 알림 표시
    showNotification(type, message, duration = 5000) {
      this.notification = {
        show: true,
        type,
        message,
      };

      setTimeout(() => {
        this.closeNotification();
      }, duration);
    },

    // 알림 닫기
    closeNotification() {
      this.notification.show = false;
    },

    // 알림 아이콘 가져오기
    getNotificationIcon(type) {
      const icons = {
        success: "fas fa-check-circle",
        error: "fas fa-exclamation-circle",
        warning: "fas fa-exclamation-triangle",
        info: "fas fa-info-circle",
      };
      return icons[type] || icons.info;
    },

    // ======= 기존 SalesPage 메서드들 =======

    checkAdminPermission() {
      const loginUser = localStorage.getItem("loginUser");
      console.log("Sales 페이지에서 확인한 loginUser:", loginUser);
      const adminUsers = ["springsoft", "tosstest"];
      this.isAdmin = adminUsers.includes(loginUser);

      console.log("isAdmin 결과:", this.isAdmin);

      if (!this.isAdmin) {
        alert("관리자 권한이 필요합니다.");
        this.$router.push("/");
      }
    },

    // 토스페이먼츠 초기화
    initTossPayments() {
      const clientKey = "test_ck_d46qopOB89dp4bmNE1QarZmM75y0"; // 테스트키

      if (typeof TossPayments !== "undefined") {
        // eslint-disable-next-line no-undef
        this.tossPayments = TossPayments(clientKey);
      } else {
        console.warn("토스페이먼츠 SDK가 로드되지 않았습니다.");
      }
    },

    // URL 파라미터 확인 (결제 결과)
    checkUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get("success");
      const fail = urlParams.get("fail");
      const authKey = urlParams.get("authKey");
      const customerKey = urlParams.get("customerKey");
      const code = urlParams.get("code");
      const message = urlParams.get("message");

      console.log("URL 파라미터 상세 확인:", {
        success,
        fail,
        authKey,
        customerKey,
        code,
        message,
        전체URL: window.location.href,
        모든파라미터: Object.fromEntries(urlParams.entries()),
      });

      if (success === "true" || success === "billing") {
        console.log("✅ 빌링키 발급 성공");
        const actualAuthKey = authKey || "billing_" + Date.now();
        const actualCustomerKey =
          customerKey || this.customerKey || "customer_mock";

        console.log("🔑 실제 사용할 값:", {actualAuthKey, actualCustomerKey});
        this.handleBillingSuccess(actualAuthKey, actualCustomerKey);
      } else if (fail === "true" || fail === "billing") {
        console.log("❌ 빌링키 발급 실패 감지");

        // 실패 원인 상세 분석
        let errorMessage = "빌링키 발급에 실패했습니다.";

        if (code) {
          errorMessage = this.getTossErrorMessage(code) || `오류 코드: ${code}`;
        } else if (message) {
          errorMessage = decodeURIComponent(message);
        }

        console.log("💡 실패 원인:", errorMessage);

        // 사용자에게 대안 제시
        const retry = confirm(
          `${errorMessage}\n\n다른 방법으로 시도하시겠습니까?\n\n확인: 다른 테스트 카드 시도\n취소: 시뮬레이션 모드로 진행`
        );

        if (retry) {
          // URL 파라미터 정리하고 다시 시도
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          alert(
            "다음 카드로 재시도해보세요:\n\n📋 법인카드 (추천)\n카드번호: 5105105105105100\n유효기간: 12/30\nCVC: 123\n\n📋 개인카드\n카드번호: 4242424242424242\n유효기간: 12/30\nCVC: 123"
          );
        } else {
          // 시뮬레이션 모드로 진행
          console.log("🎭 사용자가 시뮬레이션 선택");
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          setTimeout(() => {
            this.simulateBillingAuth();
          }, 500);
        }
      }
    },

    // 정기결제 등록
    async createSubscription() {
      if (!this.isFormValid) {
        this.validateForm();
        return;
      }

      this.isCreatingSubscription = true;
      this.setPaymentProcessing(true); // 결제 로딩 표시

      try {
        await this.requestBillingAuth();
      } catch (error) {
        console.error("정기결제 등록 실패:", error);
        this.errorMessage =
          error.message || "정기결제 등록 중 오류가 발생했습니다.";
        this.showNotification("error", this.errorMessage); // 에러 알림
        this.showErrorPopup = true;
      } finally {
        this.isCreatingSubscription = false;
        this.setPaymentProcessing(false); // 결제 로딩 해제
      }
    },

    // 빌링키 발급 요청
    async requestBillingAuth() {
      this.customerKey = this.generateCustomerKey();

      if (this.tossPayments) {
        // 실제 토스페이먼츠 빌링키 발급
        await this.tossPayments.requestBillingAuth("카드", {
          customerKey: this.customerKey,
          successUrl: `${window.location.origin}${window.location.pathname}?success=billing`,
          failUrl: `${window.location.origin}${window.location.pathname}?fail=billing`,
          customerEmail: "customer@example.com",
          customerName: this.subscriptionForm.organizationName,
        });
      } else {
        // 시뮬레이션 (개발환경)
        await this.simulateBillingAuth();
      }
    },

    // 빌링키 발급 시뮬레이션
    simulateBillingAuth() {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (Math.random() > 0.1) {
            // 90% 성공률
            const mockAuthKey = "auth_" + Date.now();
            this.handleBillingSuccess(mockAuthKey, this.customerKey);
            resolve();
          } else {
            reject(new Error("카드 인증에 실패했습니다."));
          }
        }, 2000);
      });
    },

    // 빌링키 발급 성공 처리
    handleBillingSuccess(authKey, customerKey) {
      this.billingKey = authKey;
      this.customerKey = customerKey;
      this.subscriptionCreated = true;
      this.showSuccessPopup = true;

      // 성공 알림 표시
      this.showNotification("success", "정기결제가 성공적으로 등록되었습니다!");

      // 서버에 정기결제 정보 저장
      this.saveSubscriptionToServer();
    },

    // 빌링키 발급 실패 처리
    handleBillingFailure(message) {
      this.errorMessage = message;
      this.showErrorPopup = true;
    },

    // 서버에 정기결제 정보 저장
    async saveSubscriptionToServer() {
      const subscriptionData = {
        customerKey: this.customerKey,
        billingKey: this.billingKey,
        monthlyAmount: this.monthlyAmount,
        paymentPeriod: this.paymentPeriod,
        organizationName: this.subscriptionForm.organizationName,
        billingDay: 1, // 매월 1일
        status: "active",
        createdAt: new Date().toISOString(),
      };

      console.log("정기결제 정보 저장:", subscriptionData);

      // 실제 구현시 API 호출
      try {
        // const response = await this.$http.post('/api/subscriptions', subscriptionData);
        // console.log('저장 완료:', response.data);
      } catch (error) {
        console.error("저장 실패:", error);
      }
    },

    // 폼 유효성 검사
    validateForm() {
      if (!this.subscriptionForm.organizationName.trim()) {
        alert("기관이름을 입력해주세요.");
        return false;
      }

      return true;
    },

    // 즉시 결제 테스트 (추가 기능)
    testPayment() {
      alert(
        `${this.formatCurrency(
          this.monthlyAmount
        )} 즉시 결제 테스트를 시작합니다.`
      );
      // 실제 즉시 결제 로직 구현
    },

    // 팝업 닫기
    closePopup(type) {
      if (type === "success") {
        this.showSuccessPopup = false;
      } else {
        this.showErrorPopup = false;
      }
    },

    // 폼 리셋
    resetForm() {
      this.subscriptionForm = {
        monthlyAmount: 160000, // 고정값 유지
        paymentPeriod: 60, // 고정값 유지
        organizationName: "",
      };
      this.subscriptionCreated = false;
      this.billingKey = "";
      this.customerKey = "";

      // URL 파라미터 제거
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  },
};
</script>

<style scoped>
.subscription-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f4f8e2 0%, #c2e2fe 100%);
  padding: 20px;
  padding-top: 100px;
}

.container {
  max-width: 428px;
  margin: 0 auto;
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: fadeInUp 0.8s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header {
  background: linear-gradient(135deg, #5d6ebb 0%, #d2c9e8 100%);
  color: white;
  padding: 40px 24px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.header::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 70%
  );
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
  }
}

.header h1 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
}

.header p {
  font-size: 16px;
  opacity: 0.9;
  position: relative;
  z-index: 1;
}

.form-section {
  background: white;
  padding: 35px;
  margin: 0;
}

.form-section h3 {
  margin: 0 0 30px 0;
  color: #2c3e50;
  font-size: 22px;
  font-weight: 600;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 15px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 25px;
  margin-bottom: 25px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  margin-bottom: 10px;
  font-weight: 600;
  color: #374151;
  font-size: 15px;
}

.form-input {
  padding: 14px 18px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #f9fafb;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  transform: translateY(-2px);
}

.form-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
}

.subscription-summary {
  background: #f0fdf4;
  border: 2px solid #bbf7d0;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 25px;
  animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.subscription-summary h4 {
  margin: 0 0 20px 0;
  color: #15803d;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
}

.summary-grid {
  display: grid;
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: white;
  border-radius: 8px;
  border: 1px solid #bbf7d0;
}

.summary-item .label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.summary-item .value {
  font-size: 14px;
  color: #15803d;
  font-weight: 700;
}

.form-actions {
  margin-top: 35px;
  text-align: center;
}

.btn-create-subscription {
  padding: 18px 36px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
}

.btn-create-subscription:hover:not(:disabled) {
  background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(59, 130, 246, 0.4);
}

.btn-create-subscription:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.subscription-result {
  background: white;
  padding: 35px;
  border-top: 1px solid #e9ecef;
}

.subscription-result h4 {
  margin: 0 0 25px 0;
  color: #10b981;
  font-size: 20px;
  font-weight: 600;
}

.result-box {
  background: #f0fdf4;
  border: 2px solid #bbf7d0;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 25px;
}

.result-details {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.result-details strong {
  color: #1f2937;
  font-size: 16px;
}

.result-details .amount {
  color: #10b981;
  font-weight: 700;
  font-size: 16px;
}

.result-details .period {
  color: #6b7280;
  font-style: italic;
}

.billing-info {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #bbf7d0;
}

.billing-info p {
  margin: 5px 0;
  font-size: 13px;
  color: #6b7280;
  font-family: monospace;
}

.result-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-reset,
.btn-test {
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
}

.btn-reset {
  background: #6b7280;
  color: white;
}

.btn-reset:hover {
  background: #4b5563;
  transform: translateY(-2px);
}

.btn-test {
  background: #f59e0b;
  color: white;
}

.btn-test:hover {
  background: #d97706;
  transform: translateY(-2px);
}

/* 팝업 스타일 */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.popup {
  background: white;
  border-radius: 24px;
  padding: 32px 24px;
  max-width: 320px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: popupAppear 0.3s ease-out;
}

@keyframes popupAppear {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.popup-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.popup.success .popup-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.popup.error .popup-icon {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.popup h3 {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #1f2937;
}

.popup p {
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 24px;
  line-height: 1.5;
}

.popup-button {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.popup.success .popup-button {
  background: #10b981;
  color: white;
}

.popup.error .popup-button {
  background: #ef4444;
  color: white;
}

.popup-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

@media screen and (max-width: 430px) {
  .container {
    margin: 10px auto;
    border-radius: 20px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .form-section {
    padding: 25px 20px;
  }

  .result-actions {
    flex-direction: column;
  }

  .result-actions .btn-text {
    display: none;
  }
}

/* Safe Area 지원 */
@supports (padding: max(0px)) {
  .container {
    padding-left: max(20px, env(safe-area-inset-left));
    padding-right: max(20px, env(safe-area-inset-right));
  }
}

/* iPhone 14 Pro Max 최적화 */
@media screen and (device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) {
  .container {
    margin: 10px auto;
    border-radius: 20px;
    transform: scale(1.03);
    transform-origin: top center;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .form-section {
    padding: 28px 22px;
  }

  .result-actions {
    flex-direction: column;
  }

  .result-actions .btn-text {
    display: none;
  }

  .form-input,
  .btn-create-subscription,
  .popup-button {
    font-size: 18px;
  }

  .header h1 {
    font-size: 28px;
  }

  .header p {
    font-size: 18px;
  }
}
/* 토스페이먼츠 결제 로딩 오버레이 */
.payment-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
}

.payment-loading-content {
  background: white;
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  max-width: 320px;
  width: 90%;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4c6ef5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.payment-loading-content h3 {
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 18px;
}

.payment-loading-content p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

/* 전역 알림 메시지 */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 1000;
  min-width: 300px;
  max-width: 400px;
  border-left: 4px solid;
}

.notification.success {
  border-left-color: #10b981;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
}

.notification.error {
  border-left-color: #ef4444;
  background: linear-gradient(135deg, #fef2f2 0%, #fef1f1 100%);
}

.notification.warning {
  border-left-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.notification.info {
  border-left-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
}

.notification i {
  font-size: 18px;
}

.notification.success i {
  color: #10b981;
}

.notification.error i {
  color: #ef4444;
}

.notification.warning i {
  color: #f59e0b;
}

.notification.info i {
  color: #3b82f6;
}

.notification span {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.notification-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #9ca3af;
  transition: all 0.2s;
}

.notification-close:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #6b7280;
}

/* 알림 트랜지션 */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100px);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100px);
}
</style>
